import { ColSelect } from './shared/ColSelect.jsx';
import { MultiCols } from './shared/MultiCols.jsx';
import { SourceSelect } from './shared/SourceSelect.jsx';
import { KVRows } from './shared/KVRows.jsx';
import { DropNullsInspector } from './cleaning/DropNullsInspector.jsx';
import { FillNullsInspector } from './cleaning/FillNullsInspector.jsx';
import { TrimInspector } from './cleaning/TrimInspector.jsx';
import { CaseInspector } from './cleaning/CaseInspector.jsx';
import { StripCharsInspector } from './cleaning/StripCharsInspector.jsx';
import { CastTypeInspector } from './parsing/CastTypeInspector.jsx';
import { ParseDateInspector } from './parsing/ParseDateInspector.jsx';
import { ParseNumberInspector } from './parsing/ParseNumberInspector.jsx';
import { ParseBoolInspector } from './parsing/ParseBoolInspector.jsx';
import { ComputeInspector } from './transform/ComputeInspector.jsx';
import { CaseWhenInspector } from './transform/CaseWhenInspector.jsx';
import { RegexExtractInspector } from './transform/RegexExtractInspector.jsx';
import { HashInspector } from './transform/HashInspector.jsx';
import { LookupInspector } from './transform/LookupInspector.jsx';
import { RenameInspector } from './columns/RenameInspector.jsx';
import { DropColumnsInspector } from './columns/DropColumnsInspector.jsx';
import { ReorderInspector } from './columns/ReorderInspector.jsx';
import { SplitColumnInspector } from './columns/SplitColumnInspector.jsx';
import { MergeColumnsInspector } from './columns/MergeColumnsInspector.jsx';
import { SortInspector } from './sort/SortInspector.jsx';
import { TopNInspector } from './sort/TopNInspector.jsx';
import { RegexValidateInspector } from './validate/RegexValidateInspector.jsx';
import { RangeCheckInspector } from './validate/RangeCheckInspector.jsx';
import { AssertInspector } from './validate/AssertInspector.jsx';
import { GroupByInspector } from './aggregate/GroupByInspector.jsx';
import { WindowInspector } from './aggregate/WindowInspector.jsx';
import { RollingInspector } from './aggregate/RollingInspector.jsx';
import { ConditionalGroupInspector } from './aggregate/ConditionalGroupInspector.jsx';
import { HttpRequestInspector } from './enrich/HttpRequestInspector.jsx';
import { JoinInspector } from './joins/JoinInspector.jsx';
import { UnionInspector } from './joins/UnionInspector.jsx';
import { FuzzyInspector } from './joins/FuzzyInspector.jsx';
import { PivotInspector } from './reshape/PivotInspector.jsx';
import { UnpivotInspector } from './reshape/UnpivotInspector.jsx';
import { ExplodeInspector } from './reshape/ExplodeInspector.jsx';
import { SampleInspector } from './sampling/SampleInspector.jsx';
import { SplitDatasetInspector } from './sampling/SplitDatasetInspector.jsx';
import { GeocodeInspector } from './enrich/GeocodeInspector.jsx';
import { CurrencyInspector } from './enrich/CurrencyInspector.jsx';
import { ApiLookupInspector } from './enrich/ApiLookupInspector.jsx';
import { SentimentInspector } from './enrich/SentimentInspector.jsx';
import { ClassifyInspector } from './enrich/ClassifyInspector.jsx';
import { ProfileColumnsInspector } from './profile/ProfileColumnsInspector.jsx';
import { ProfileOutliersInspector } from './profile/ProfileOutliersInspector.jsx';
import { PlaceholderInspector } from './shared/PlaceholderInspector.jsx';
import { LeftTrimInspector } from './cleaning/LeftTrimInspector.jsx';
import { RightTrimInspector } from './cleaning/RightTrimInspector.jsx';
import { MidInspector } from './cleaning/MidInspector.jsx';
import { FindReplaceInspector } from './cleaning/FindReplaceInspector.jsx';
import { MinMaxScalingInspector } from './feature_engineering/MinMaxScalingInspector.jsx';
import { StandardScalingInspector } from './feature_engineering/StandardScalingInspector.jsx';
import { RobustScalingInspector } from './feature_engineering/RobustScalingInspector.jsx';
import { OrdinalEncodingInspector } from './feature_engineering/OrdinalEncodingInspector.jsx';
import { OneHotEncodingInspector } from './feature_engineering/OneHotEncodingInspector.jsx';
import { FrequencyEncodingInspector } from './feature_engineering/FrequencyEncodingInspector.jsx';
import { FixedClippingInspector } from './feature_engineering/FixedClippingInspector.jsx';
import { PercentileBoundsInspector } from './feature_engineering/PercentileBoundsInspector.jsx';
import { IqrOutlierInspector } from './feature_engineering/IqrOutlierInspector.jsx';
import { ZScoreOutlierInspector } from './feature_engineering/ZScoreOutlierInspector.jsx';
import { VarianceThresholdInspector } from './feature_engineering/VarianceThresholdInspector.jsx';
import { CorrelationInspector } from './feature_engineering/CorrelationInspector.jsx';
import { Chi2Inspector } from './feature_engineering/Chi2Inspector.jsx';
import { AnovaInspector } from './feature_engineering/AnovaInspector.jsx';
import { TextStructuralMetricsInspector } from './feature_engineering/TextStructuralMetricsInspector.jsx';
import { SkewCorrectionInspector } from './feature_engineering/SkewCorrectionInspector.jsx';

// ─── Specific inspectors for the expanded step catalog ─────────────────
// Registered at the bottom into `window.INSPECTORS` so StepInspector can
// look up the right component per step type. Keep each inspector compact —
// most of them follow the same pattern: pick a column, set a parameter.

// Re-exported so this module's public surface is unchanged by the split.
export { ColSelect, MultiCols, SourceSelect, KVRows, PlaceholderInspector };

// ── Register all dispatchers ───────────────────────────────────────────
export const INSPECTORS = {
  // Cleaning
  drop_nulls: DropNullsInspector,
  fill_nulls: FillNullsInspector,
  trim: TrimInspector,
  left_trim: LeftTrimInspector,
  right_trim: RightTrimInspector,
  case: CaseInspector,
  strip_chars: StripCharsInspector,
  mid: MidInspector,
  find_replace: FindReplaceInspector,
  // Type & parse
  cast_type: CastTypeInspector,
  parse_date: ParseDateInspector,
  parse_number: ParseNumberInspector,
  parse_bool: ParseBoolInspector,
  // Row transforms
  compute: ComputeInspector,
  case_when: CaseWhenInspector,
  regex_extract: RegexExtractInspector,
  hash: HashInspector,
  lookup: LookupInspector,
  // Columns
  rename: RenameInspector,
  drop_columns: DropColumnsInspector,
  reorder: ReorderInspector,
  split_column: SplitColumnInspector,
  merge_columns: MergeColumnsInspector,
  // Sort & rank
  sort: SortInspector,
  top_n: TopNInspector,
  // Aggregate
  group_by: GroupByInspector,
  window: WindowInspector,
  rolling: RollingInspector,
  conditional_group: ConditionalGroupInspector,
  http_request: HttpRequestInspector,
  // Join
  join: JoinInspector,
  union: UnionInspector,
  fuzzy_match: FuzzyInspector,
  // Reshape
  pivot: PivotInspector,
  unpivot: UnpivotInspector,
  explode: ExplodeInspector,
  // Validate
  regex_validate: RegexValidateInspector,
  range_check: RangeCheckInspector,
  assert: AssertInspector,
  // Enrich
  geocode: GeocodeInspector,
  currency: CurrencyInspector,
  api_lookup: ApiLookupInspector,
  sentiment: SentimentInspector,
  classify: ClassifyInspector,
  // Feature Engineering
  // Feature Engineering - Scaling
  min_max_scaling: MinMaxScalingInspector,
  standard_scaling: StandardScalingInspector,
  robust_scaling: RobustScalingInspector,
  // Feature Engineering - Encoding
  ordinal_encoding: OrdinalEncodingInspector,
  one_hot_encoding: OneHotEncodingInspector,
  frequency_encoding: FrequencyEncodingInspector,
  // Feature Engineering - Outlier Handling
  fixed_clipping: FixedClippingInspector,
  percentile_bounds: PercentileBoundsInspector,
  iqr_outlier: IqrOutlierInspector,
  zscore_outlier: ZScoreOutlierInspector,
  // Feature Engineering - Feature Selection
  variance_threshold: VarianceThresholdInspector,
  pearson_correlation: CorrelationInspector,
  spearman_correlation: CorrelationInspector,
  kendall_correlation: CorrelationInspector,
  chi2_selection: Chi2Inspector,
  anova_selection: AnovaInspector,
  // Feature Engineering - Text Features & Extraction
  text_structural_metrics: TextStructuralMetricsInspector,
  // Feature Engineering - Distribution & Skew Correction
  skew_correction_step: SkewCorrectionInspector,
  // Sample / split
  sample: SampleInspector,
  split_dataset: SplitDatasetInspector,
  // Profile
  profile_columns: ProfileColumnsInspector,
  profile_outliers: ProfileOutliersInspector,
};
